import { createFileRoute } from '@tanstack/react-router'
import type { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'

import type { PointOfSaleListItem } from '@/api/admin-sales-channels-types'
import { preloadListPointsOfSale, useListPointsOfSale } from '@/api/react-query'
import { Badge } from '@/components/base/badge'
import { Container, ContainerHeader } from '@/components/base/container'
import { SearchField } from '@/components/base/search-field'
import { SingleColumnPage } from '@/components/page-layout'
import { Table } from '@/components/table/table'
import { useI18n } from '@/i18n/i18n-react'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/settings/points-of-sale/devices/')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={[]}>
			<DevicesList />
		</ConditionalRouteComponent>
	),
	loader: async ({ context }) => await preloadListPointsOfSale(context, {})
})

function DevicesList() {
	const { L } = useI18n()
	const pointsOfSale = useListPointsOfSale()

	const columnDefs = useMemo<ColDef<PointOfSaleListItem>[]>(
		() => [
			{
				headerName: L.Fields.Name(),
				field: 'name',
				flex: 2
			},
			{
				headerName: L.Fields.SalesChannel(),
				field: 'salesChannels',
				flex: 2,
				cellRenderer: ({ value }: { value: Array<{ uuid: string; name: string }> }) => (
					<div className='flex h-full items-center gap-1'>
						{value.map(channel => (
							<Badge key={channel.uuid} size='2xsmall'>
								{channel.name}
							</Badge>
						))}
					</div>
				)
			}
		],
		[L]
	)

	return (
		<SingleColumnPage hasOutlet>
			<Container className='flex flex-1 flex-col overflow-hidden'>
				<ContainerHeader heading={L.Settings.Devices.Title()} rightSide={<SearchField />} />
				<Table<PointOfSaleListItem>
					className='w-full flex-1'
					data={pointsOfSale.data}
					columnUtilities={{}}
					columnDefs={columnDefs}
					rowSelection={{
						mode: 'singleRow',
						checkboxes: false,
						enableClickSelection: false,
						enableSelectionWithoutKeys: false
					}}
					suppressCellFocus
					rowToRouterConfig={row => ({
						to: '/settings/points-of-sale/devices/$pointOfSaleUuid',
						params: { pointOfSaleUuid: row.uuid }
					})}
				/>
			</Container>
		</SingleColumnPage>
	)
}
